import React, { useState, useRef, useEffect, useMemo } from 'react';
import ExcelTable from './ExcelTable';
import { tableAPI } from '../services/api';
import './Table.css';

const Table3 = () => {
  // Load saved name from localStorage or use default
  const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
  const [tableName, setTableName] = useState(savedNames[3] || 'Stock Updates');
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectionStats, setSelectionStats] = useState(null);

  // Sync table name from localStorage when it changes
  useEffect(() => {
    const handleNameUpdate = () => {
      const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
      const newName = savedNames[3] || 'Stock Updates';
      setTableName(prevName => {
        if (prevName !== newName) {
          return newName;
        }
        return prevName;
      });
    };
    
    window.addEventListener('defaultTableNameUpdated', handleNameUpdate);
    return () => {
      window.removeEventListener('defaultTableNameUpdated', handleNameUpdate);
    };
  }, []);

  // Initialize with empty data - ONLY headers
  // Data should come from MySQL via ExcelTable component
  const getInitialData = () => {
    // Return empty table with headers only
    // ExcelTable will load actual data from MySQL
    // Date moved to after S no
    return [['S no', 'Date', 'Name', 'Source', 'Buying Rate', 'Amount', 'Currency', 'Notes']];
  };

  const initialData = getInitialData();

  // Currency column is at index 6 (0-indexed) - after Date moved to index 1
  const currencyColumns = [6];
  // Total columns: amount (5) - removed, shown as stats above title
  const totalColumns = [];
  
  // Calculate totals from table data
  // Fetch from MySQL first, fallback to localStorage
  const [tableDataForTotals, setTableDataForTotals] = useState(null);
  
  useEffect(() => {
    const fetchDataForTotals = async () => {
      try {
        // Try MySQL first
        const mysqlData = await tableAPI.getById('table3');
        if (mysqlData && mysqlData.length > 1) {
          setTableDataForTotals(mysqlData);
          // Sync to localStorage
          localStorage.setItem('table_table3', JSON.stringify(mysqlData));
        } else {
          // MySQL empty - clear localStorage and set empty
          localStorage.removeItem('table_table3');
          setTableDataForTotals(null);
        }
      } catch (error) {
        // MySQL failed - try localStorage but validate
        const localData = localStorage.getItem('table_table3');
        if (localData) {
          try {
            const parsed = JSON.parse(localData);
            if (parsed && parsed.length > 1) {
              setTableDataForTotals(parsed);
            } else {
              localStorage.removeItem('table_table3');
              setTableDataForTotals(null);
            }
          } catch (e) {
            localStorage.removeItem('table_table3');
            setTableDataForTotals(null);
          }
        } else {
          setTableDataForTotals(null);
        }
      }
    };
    
    fetchDataForTotals();
    // Refresh every 2 seconds to sync with MySQL
    const interval = setInterval(fetchDataForTotals, 2000);
    return () => clearInterval(interval);
  }, []);
  
  const calculateTotals = () => {
    try {
      // Use fetched data from MySQL
      const parsed = tableDataForTotals;
      if (!parsed || parsed.length < 2) return { totalAmount: 0, totalsByCurrency: {}, hasData: false };
      
      const headers = parsed[0];
      const amountIndex = headers.indexOf('Amount');
      const currencyIndex = headers.indexOf('Currency');
      
      let totalAmount = 0;
      const totalsByCurrency = {};
      let hasData = false;
      
      parsed.slice(1).forEach(row => {
        if (row && row.length > 0 && amountIndex !== -1 && row[amountIndex]) {
          const amount = parseFloat(row[amountIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
          
          if (amount !== 0) {
            totalAmount += amount;
            hasData = true;
            
            // Group by currency
            if (currencyIndex !== -1 && row[currencyIndex]) {
              const currency = String(row[currencyIndex]).toUpperCase().trim();
              if (currency) {
                if (!totalsByCurrency[currency]) {
                  totalsByCurrency[currency] = 0;
                }
                totalsByCurrency[currency] += amount;
              }
            }
          }
        }
      });
      
      return { totalAmount, totalsByCurrency, hasData };
    } catch (e) {
      console.error('Error calculating totals:', e);
      return { totalAmount: 0, totalsByCurrency: {}, hasData: false };
    }
  };

  const totals = useMemo(() => calculateTotals(), [tableDataForTotals]);

  // Reload totals when table data changes (check for actual changes)
  // DISABLED: Don't reload from localStorage every 500ms - data comes from MySQL
  // This was causing data to disappear after loading from MySQL
  // useEffect(() => {
  //   let lastDataHash = '';
  //   const interval = setInterval(() => {
  //     const currentData = localStorage.getItem('table_table3');
  //     const currentHash = currentData ? JSON.stringify(currentData).slice(0, 1000) : '';
  //     
  //     if (currentHash !== lastDataHash) {
  //       lastDataHash = currentHash;
  //       setRefreshKey(prev => prev + 1);
  //     }
  //   }, 500);
  //   return () => clearInterval(interval);
  // }, []);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleNameDoubleClick = () => {
    setIsEditingName(true);
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    // Save updated name to localStorage for sidebar sync
    const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
    savedNames[3] = tableName; // Table3 has id 3
    localStorage.setItem('defaultTableNames', JSON.stringify(savedNames));
    // Dispatch custom event to notify sidebar
    window.dispatchEvent(new Event('defaultTableNameUpdated'));
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsEditingName(false);
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  return (
    <div className="table-page">
      <div className="table-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {isEditingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                className="table-name-input"
                maxLength={50}
              />
            ) : (
              <h1 onDoubleClick={handleNameDoubleClick} className="table-name-editable" title="Double-click to edit" style={{ margin: 0 }}>
                {tableName}
                <span className="edit-icon">✎</span>
              </h1>
            )}
            {totals.hasData && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', marginLeft: 'auto' }}>
                {Object.keys(totals.totalsByCurrency).length > 0 ? (
                  // Show totals grouped by currency
                  Object.entries(totals.totalsByCurrency)
                    .sort(([a], [b]) => {
                      // Sort: INR first, then AED, then alphabetically
                      if (a === 'INR') return -1;
                      if (b === 'INR') return 1;
                      if (a === 'AED') return -1;
                      if (b === 'AED') return 1;
                      return a.localeCompare(b);
                    })
                    .map(([currency, amount]) => (
                      <div key={currency} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>
                          {currency}:
                        </span>
                        <span style={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: '700' }}>
                          {currency === 'INR' || currency === 'AED' 
                            ? (currency === 'INR' ? '₹' : currency === 'AED' ? 'AED ' : currency + ' ')
                            : currency + ' '}
                          {amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        {selectionStats && selectionStats[5] && selectionStats[5].hasNumeric && selectionStats[5].byCurrency && selectionStats[5].byCurrency[currency] && (
                          <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span>Selected {currency}: {currency === 'INR' ? '₹' : currency === 'AED' ? 'AED ' : currency + ' '}{selectionStats[5].byCurrency[currency].sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: {currency === 'INR' ? '₹' : currency === 'AED' ? 'AED ' : currency + ' '}{selectionStats[5].byCurrency[currency].average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                      </div>
                    ))
                ) : (
                  // Fallback: Show INR if no currency breakdown
                  totals.totalAmount !== 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>INR:</span>
                    <span style={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: '700' }}>
                      ₹{totals.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    {selectionStats && selectionStats[5] && selectionStats[5].hasNumeric && selectionStats[5].byCurrency && (
                      <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {selectionStats[5].byCurrency.INR && (
                          <>
                          <span>Selected INR: ₹{selectionStats[5].byCurrency.INR.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[5].byCurrency.INR.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </>
                        )}
                        {selectionStats[5].byCurrency.AED && (
                          <>
                          <span>Selected AED: AED {selectionStats[5].byCurrency.AED.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: AED {selectionStats[5].byCurrency.AED.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <ExcelTable 
        initialData={initialData} 
        currencyColumns={currencyColumns}
        fixedHeaders={true}
        totalColumns={totalColumns}
        tableId="table3"
        tableName={tableName}
        onSelectionStatsChange={setSelectionStats}
      />
    </div>
  );
};

export default Table3;

